/**
 * @file OrderStagesPage.tsx
 * @description «Стадии заказов» — раздел «Настроек» (phase10.5 шаг 8;
 * phase19.31 — общий редактор `StageListEditor`, компактный список).
 *
 * Страница — только данные для редактора: откуда грузить воронку и стадии,
 * как сохранить порядок, как удалить, какое окно правки открыть. Запреты
 * (удалить или сменить вид стадии с заказами нельзя) проверяет сервер, а
 * редактор и окно правки их показывают.
 */

import { useParams } from "react-router";

import { useHasPermission } from "../../../auth/hooks";
import { OrderStageEditDialog } from "../../../components/orders/OrderStageEditDialog";
import type { StageListConfig } from "../../../components/orders/stages/stage-list-types";
import { StageListEditor } from "../../../components/orders/stages/StageListEditor";
import {
  deleteOrderStage,
  listOrderPipelines,
  listOrderStages,
  reorderOrderStages,
  type OrderStageConfigDto,
} from "../../../lib/api-orders";

const ORDER_STAGES: StageListConfig<OrderStageConfigDto> = {
  title: "Стадии заказов",
  countOf: (stage) => stage.ordersCount,
  countWords: ["заказ", "заказа", "заказов"],
  // Отдельного GET воронки по id нет — название берём из общего списка.
  load: async (pipelineId) => {
    const [pipelines, stages] = await Promise.all([listOrderPipelines(), listOrderStages(pipelineId)]);
    return { pipelineName: pipelines.find((p) => p.id === pipelineId)?.name ?? null, stages };
  },
  reorder: reorderOrderStages,
  remove: deleteOrderStage,
  renderDialog: ({ pipelineId, state, onClose, onSaved }) => (
    <OrderStageEditDialog
      mode={state.mode}
      pipelineId={pipelineId}
      stage={state.mode === "edit" ? state.stage : undefined}
      onClose={onClose}
      onSaved={onSaved}
    />
  ),
};

export function OrderStagesPage() {
  const { pipelineId } = useParams<{ pipelineId: string }>();
  const canConfig = useHasPermission("SHOP_MANAGE_ORDERS");
  return <StageListEditor config={ORDER_STAGES} pipelineId={pipelineId} canConfig={canConfig} />;
}
